/**
 * Public links for running dev servers (Run & Share, Phase 2).
 *
 * One share per run (same key as runStore: `<repo path> + <configuration>`).
 * Module-level, like runStore, so a link stays up — and the title bar badge
 * stays on — when the user leaves the project page.
 *
 *   creating      POST /tunnels in flight
 *   connecting    Rust is joining `tunnel:<id>`
 *   connected     public: requests are served
 *   reconnecting  the socket dropped; Rust re-joins the same tunnel
 *
 * A share ends when the user stops it, when its run stops or exits (watched
 * here AND in Rust via `terminal-exit`), on sign-out, or when Rust reports the
 * tunnel closed (expired, refused). `ended[key]` keeps why, for one line in
 * the row.
 */
import { create } from 'zustand';
import * as api from './shareApi';
import { isLive, useRunStore } from '../runStore';

export const LOG_LIMIT = 100;
export const USAGE_POLL_MS = 30 * 1000;

const ACTIVE = new Set(['creating', 'connecting', 'connected', 'reconnecting']);
export const isSharing = (share) => !!share && ACTIVE.has(share.state);
/** The link is reachable (or about to be) — what the title bar badge shows. */
export const isPublic = (share) => !!share && share.tunnelId != null && ACTIVE.has(share.state);

export const useShareStore = create(() => ({ shares: {}, ended: {}, disabled: false, usage: null }));

const byTunnel = new Map(); // tunnelId -> run key
let unlisteners = null;
let listening = null;
let usageTimer = null;

function patch(key, fields) {
  useShareStore.setState((s) => {
    const prev = s.shares[key];
    if (!prev) return s;
    return { shares: { ...s.shares, [key]: { ...prev, ...fields } } };
  });
}

function remove(key, endedReason) {
  useShareStore.setState((s) => {
    if (!s.shares[key] && !endedReason) return s;
    const shares = { ...s.shares };
    delete shares[key];
    const ended = { ...s.ended };
    if (endedReason) ended[key] = endedReason;
    else delete ended[key];
    return { shares, ended };
  });
  if (Object.keys(useShareStore.getState().shares).length === 0) stopUsagePolling();
}

function onStatus(payload) {
  const st = payload?.payload ?? payload;
  const key = byTunnel.get(String(st?.tunnelId));
  if (key == null) return;
  if (st.state === 'closed') {
    byTunnel.delete(String(st.tunnelId));
    const reason = st.reason || 'closed';
    remove(key, reason === 'stopped' ? null : reason);
    // The server may still hold it (a refusal, a lost socket): say we are done.
    if (reason !== 'signed_out') api.deleteTunnel(st.tunnelId).catch(() => {});
    return;
  }
  const prev = useShareStore.getState().shares[key];
  patch(key, {
    state: st.state,
    publicUrl: st.publicUrl || prev?.publicUrl || null,
    expiresAt: typeof st.expiresAt === 'number' ? st.expiresAt * 1000 : prev?.expiresAt ?? null,
    bytesIn: st.bytesIn ?? prev?.bytesIn ?? 0,
    bytesOut: st.bytesOut ?? prev?.bytesOut ?? 0,
    requests: st.requests ?? prev?.requests ?? 0,
  });
}

function onRequest(payload) {
  const line = payload?.payload ?? payload;
  const key = byTunnel.get(String(line?.tunnelId));
  if (key == null) return;
  const prev = useShareStore.getState().shares[key];
  if (!prev) return;
  const entry = { method: line.method, path: line.path, status: line.status, ms: line.ms, bytes: line.bytes, at: Date.now() };
  patch(key, { log: [entry, ...(prev.log || [])].slice(0, LOG_LIMIT) });
}

/** Sign-out: Rust closes every tunnel itself (sync_set_auth); forget them here. */
function onSessionEnded() {
  byTunnel.clear();
  api.closeTunnel({}).catch(() => {});
  stopUsagePolling();
  useShareStore.setState({ shares: {}, ended: {}, usage: null });
}

function ensureListeners() {
  if (!listening) {
    listening = (async () => {
      const subs = await Promise.all([
        api.listen('tunnel-status', onStatus),
        api.listen('tunnel-request', onRequest),
        // A join said `unauthorized`: refresh the token the normal way; Rust retries once.
        api.listen('tunnel-auth-refresh', () => {
          Promise.resolve()
            .then(() => api.refreshToken())
            .catch(() => {});
        }),
      ]);
      try {
        const { addSessionEndedListener } = await import('../../../../services/channelService');
        subs.push(addSessionEndedListener(onSessionEnded));
      } catch {
        /* no session events in this build */
      }
      unlisteners = subs;
    })().catch((e) => {
      listening = null;
      throw e;
    });
  }
  return listening;
}

async function refreshUsage() {
  try {
    const usage = await api.tunnelUsage();
    useShareStore.setState({ usage });
  } catch {
    /* keep the last value */
  }
}

function startUsagePolling() {
  if (usageTimer) return;
  refreshUsage();
  usageTimer = setInterval(refreshUsage, USAGE_POLL_MS);
}

function stopUsagePolling() {
  if (usageTimer) clearInterval(usageTimer);
  usageTimer = null;
}

/**
 * Make the dev server of run `key` public. Resolves `{ ok: true }` or
 * `{ ok: false, kind }` with kind `plan_limit` | `feature_disabled` | `share_suspended` |
 * `error` | `cancelled` (stopped while creating). Never throws.
 */
export async function startShare(key, { runId, port, label }) {
  const existing = useShareStore.getState().shares[key];
  if (isSharing(existing)) return { ok: true };
  const attempt = {};
  useShareStore.setState((s) => {
    const ended = { ...s.ended };
    delete ended[key];
    return {
      ended,
      shares: { ...s.shares, [key]: { attempt, state: 'creating', tunnelId: null, runId, port, label, publicUrl: null, expiresAt: null, bytesIn: 0, bytesOut: 0, requests: 0, log: [] } },
    };
  });
  const current = () => useShareStore.getState().shares[key]?.attempt === attempt;

  let tunnel;
  try {
    tunnel = await api.createTunnel(`${label} — localhost:${port}`);
  } catch (e) {
    const kind = api.createErrorKind(e);
    if (current()) remove(key);
    if (kind === 'feature_disabled') useShareStore.setState({ disabled: true });
    if (kind === 'share_suspended') {
      useShareStore.setState((s) => ({ ended: { ...s.ended, [key]: 'share_suspended' } }));
    }
    return { ok: false, kind, message: e?.response?.data?.message || e?.message || '' };
  }
  const id = tunnel?.id != null ? String(tunnel.id) : null;
  if (!current() || !isLive(useRunStore.getState().runs[key]) || !id) {
    // Stopped (or the server sent no id) while the link was being created.
    if (id) api.deleteTunnel(id).catch(() => {});
    if (current()) remove(key);
    return { ok: false, kind: id ? 'cancelled' : 'error' };
  }
  const parsed = Date.parse(tunnel.expires_at);
  patch(key, { state: 'connecting', tunnelId: id, publicUrl: tunnel.public_url || null, expiresAt: Number.isFinite(parsed) ? parsed : null });
  byTunnel.set(id, key);
  try {
    await ensureListeners();
    await api.openTunnel({
      runId: String(runId),
      port,
      label,
      tunnelId: id,
      socketUrl: await api.socketUrl(),
      publicUrl: tunnel.public_url || null,
      expiresAt: tunnel.expires_at ?? null,
    });
  } catch (e) {
    byTunnel.delete(id);
    if (current()) remove(key, 'open_failed');
    api.deleteTunnel(id).catch(() => {});
    return { ok: false, kind: 'error', message: String(e?.message || e) };
  }
  if (!current()) {
    // Stopped while Rust was starting it.
    api.closeTunnel({ tunnelId: id }).catch(() => {});
    return { ok: false, kind: 'cancelled' };
  }
  startUsagePolling();
  return { ok: true };
}

/** Stop sharing run `key`: the link stops working at once. */
export async function stopShare(key) {
  const share = useShareStore.getState().shares[key];
  if (!share) return;
  remove(key);
  const id = share.tunnelId;
  if (id == null) return; // still creating: startShare deletes it when POST returns
  byTunnel.delete(id);
  await Promise.all([api.closeTunnel({ tunnelId: id }).catch(() => {}), api.deleteTunnel(id).catch(() => {})]);
}

export function dismissEnded(key) {
  useShareStore.setState((s) => {
    if (!s.ended[key]) return s;
    const ended = { ...s.ended };
    delete ended[key];
    return { ended };
  });
}

// A dev server that stops (or is being stopped) is not shared any more.
useRunStore.subscribe((s) => {
  const { shares } = useShareStore.getState();
  Object.keys(shares).forEach((key) => {
    const run = s.runs[key];
    if (!isLive(run) || run.status === 'stopping') stopShare(key);
  });
});

/** Test hook. */
export function resetShareStoreForTests() {
  byTunnel.clear();
  stopUsagePolling();
  (unlisteners || []).forEach((u) => {
    try {
      u?.();
    } catch {
      /* ignore */
    }
  });
  unlisteners = null;
  listening = null;
  useShareStore.setState({ shares: {}, ended: {}, disabled: false, usage: null });
}
